import { Box, Flex, Tag, Text, Tooltip } from '@pinchenterprisesnpm/friday-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import { connect, FormikProps } from 'formik'
import { useSelector } from 'react-redux'
import { selectRosters } from '@src/features/shift-post-v2/state/shift-post-v2.selectors'
import { useMemo } from 'react'
import { getSelectedAcceptanceCriteriaOption } from '@src/features/shift-post-v2/utils'
import { ACCEPTANCE_CRITERIA_OPTION } from '@src/features/shift-post-v2/steps'
import { BadgeItem } from '@src/components/business/BadgesCell/style'
import { useInvitesHistory } from '@src/features/shift-post-v2/hooks/useInvitesHistory'
import { IInviteUpshiftersFormValues } from '../../invite-upshifters-section.types'

interface IConfirmSummaryProps {
  formik?: FormikProps<IInviteUpshiftersFormValues>
}

export const ConfirmSummary = connect<IConfirmSummaryProps>(({ formik }) => {
  const { values } = formik

  const selectedAutoAcceptOption = getSelectedAcceptanceCriteriaOption(
    values.auto_accept_by_rosters,
    values.auto_accept,
  )
  const isAAByRosters =
    selectedAutoAcceptOption ===
    ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS

  const rosters = useSelector(selectRosters)

  const {
    pendingRosterIds,
    finishedRosterIds,
    getBadgeProps,
  } = useInvitesHistory()

  const selectedRosters = useMemo(() => {
    const filteredRosters = rosters.filter(roster =>
      values.roster_ids.includes(roster.id),
    )

    if (isAAByRosters) {
      // Create a Map for rosters lookups
      const rosterMap = new Map(
        filteredRosters.map(roster => [roster.id, roster]),
      )

      // Get rosters in the specified order
      const orderedRosters = (values.roster_ids ?? [])
        .map(rosterId => rosterMap.get(rosterId))
        .filter(Boolean) as typeof filteredRosters

      const availableRosters: typeof filteredRosters = []
      const unavailableRosters: typeof filteredRosters = []

      orderedRosters.forEach(roster => {
        if (roster.is_available_for_invite === false) {
          unavailableRosters.push(roster)
        } else {
          availableRosters.push(roster)
        }
      })

      return [...availableRosters, ...unavailableRosters]
    } else {
      const availableRosters: typeof filteredRosters = []
      const unavailableRosters: typeof filteredRosters = []

      filteredRosters.forEach(roster => {
        if (roster.is_available_for_invite === false) {
          unavailableRosters.push(roster)
        } else {
          availableRosters.push(roster)
        }
      })

      return [...availableRosters, ...unavailableRosters]
    }
  }, [rosters, values.roster_ids, isAAByRosters])

  if (selectedRosters.length === 0) {
    return (
      <Text my="sm" color="muted">
        No invitations selected.
      </Text>
    )
  }

  return (
    <>
      {selectedRosters.map(item => {
        const isFinished = finishedRosterIds?.has(item.id)
        const isPending = pendingRosterIds?.has(item.id)

        const badgeProps = getBadgeProps(isFinished, isPending)

        return (
          <Flex key={item.id} mb="xs" alignItems="center">
            <FontAwesomeIcon
              icon={faCheck}
              color="#43b94a"
              size="lg"
              style={{ marginRight: '8px' }}
            />
            <Box>
              <Text>{item.name} </Text>
            </Box>
            <Text color="primary" fontWeight="semiBold" ml="xs">
              {item.members_count} invites
            </Text>
            {item.is_available_for_invite === false && (
              <Tooltip text="None of the Upshifters in this roster are eligible for an invite.">
                <span>
                  <Tag size="small" type="danger" mx="xs">
                    Unavailable
                  </Tag>
                </span>
              </Tooltip>
            )}
            {badgeProps && (
              <BadgeItem type={badgeProps.type} ml="xs">
                {badgeProps.text}
              </BadgeItem>
            )}
          </Flex>
        )
      })}
    </>
  )
})
